import { Form } from 'factorial-form'
import { makeObservable, observable } from 'mobx'
import type { IObservableArray } from 'mobx'

import { Collection } from 'modules/core/lib/mobx-rest'
import type { SetOptions } from 'modules/core/lib/mobx-rest/types'

import { GROUPABLE_ACTIVE, MEMBERS, RELATIVE_ROLE, TEAM_LEAD } from 'modules/approvals/lib/roles'
import type { IStepApprover } from 'modules/approvals/store/models/StepApprover'
import StepApprover from 'modules/approvals/store/models/StepApprover'

const STATUS = {
  created: 'created',
  deleted: 'deleted',
  updated: 'updated',
}

export class StepApproverForm extends Form {
  constructor(values: object) {
    super(values, {
      id: 'number',
      approver_type: 'string',
      approver_identifier: 'string',
      deleted: 'boolean',
      created: 'boolean',
      tobe_deleted: 'boolean',
      tobe_approver_identifier: 'string',
      tobe_approver_type: 'string',
      groupable_id: 'number',
      groupable_type: 'string',
      tobe_groupable_id: 'number',
      tobe_groupable_type: 'string',
    })
  }
}

export interface ApprovalStep {
  id?: number
  status?: string
  position: number
  approvers: [StepApproverForm]
  strategy?: string
}

interface BuiltForm {
  [key: string]: ApprovalStep
}

class StepApprovers extends Collection<StepApprover> {
  groupId?: number
  forms: IObservableArray<ApprovalStep>

  constructor(data?: IStepApprover[], groupId?: number) {
    super(data)
    makeObservable(this, {
      forms: observable,
    })
    this.groupId = groupId
    this.forms = observable.array([])
  }

  get cacheKey(): string {
    return 'stepApprovers'
  }

  private get dynamicPath() {
    return this.groupId ? `/approvals/approval_groups/${this.groupId}/default_approvers` : ''
  }

  fetch(options: SetOptions = {}) {
    const pathOptions = this.dynamicPath ? { path: this.dynamicPath } : {}
    return super.fetch({ ...options, ...pathOptions })
  }

  model() {
    return StepApprover
  }

  buildForms() {
    const builtForms = this.models.reduce((acc, approver) => {
      const approvalStepPosition = approver.approvalStepPosition.toString()
      const approverForm = new StepApproverForm({
        id: approver.id,
        approver_type: approver.role,
        // Access ID or group name
        approver_identifier:
          approver.groupName === 'members' ? approver.approverId : approver.groupName,
        groupable_id: approver.approverGroupableId,
        groupable_type: approver.approverGroupableType,
      })
      const accForm: BuiltForm = acc // assign type to acc here, else builtForm takes model type
      return {
        ...accForm,
        [approvalStepPosition]: {
          id: approver.approvalStepId,
          status: null,
          position: approver.approvalStepPosition,
          strategy: approver.approvalStepStrategy,
          approvers: [...(accForm[approvalStepPosition]?.approvers || []), approverForm],
        },
      }
    }, {})
    const formArray = Object.values(builtForms as BuiltForm)
    this.forms.replace(formArray)
  }

  addApprover(form: StepApproverForm, step: ApprovalStep) {
    step.approvers.push(form)
  }

  deleteApprover(step: ApprovalStep, approverForm: StepApproverForm) {
    if (approverForm.get('created').value) {
      const index = step.approvers.indexOf(approverForm)
      step.approvers.splice(index, 1)
    } else {
      approverForm.setValues({
        deleted: true,
        tobe_deleted: false,
      })
    }
  }

  addStep(form: StepApproverForm) {
    const formLength = this.forms.length
    const position = this.forms[formLength - 1]?.position as number
    this.forms.push({
      position: position + 1,
      status: STATUS.created,
      approvers: [form],
    })
  }

  deleteStep(formPosition: number) {
    const step = this.forms.find((form) => form.position === formPosition)
    if (!step) return null
    if (!step.status) {
      step.status = STATUS.deleted
    } else {
      this.forms.replace(this.forms.filter((form) => form != step))
    }
  }

  async saveEdits() {
    const data = this.draftFormData()
    const savedData = await this.rpc('save_edits', { data })
    this.set(savedData)
    this.buildForms()

    return savedData
  }

  draftApprovers(approvalStep: ApprovalStep) {
    return approvalStep.approvers.map((form: StepApproverForm) => {
      const approverIdentifier = form.get('approver_identifier').value
      const approverType = form.get('approver_type').value
      const id = form.get('id').value
      const isRelativeRole = approverType === RELATIVE_ROLE
      const deleted = form.get('deleted').value
      const created = form.get('created').value
      const strategy = isRelativeRole ? approverIdentifier : MEMBERS
      const groubale = [TEAM_LEAD, GROUPABLE_ACTIVE].includes(strategy)
      const groupable_id = groubale ? form.get('groupable_id').value : null
      const groupable_type = groubale ? form.get('groupable_type').value : null

      const status = () => {
        if (deleted) {
          return STATUS.deleted
        }

        if (created) {
          return STATUS.created
        }

        if (id && form.isDirty) {
          return STATUS.updated
        }

        return null
      }
      return {
        id,
        strategy,
        groupable_id,
        groupable_type,
        access_id: isRelativeRole ? null : approverIdentifier,
        status: status(),
      }
    })
  }

  draftFormData() {
    let position = -1

    return this.forms.map((approvalStep) => {
      const stepDeleted = approvalStep.status == STATUS.deleted

      const approvers = this.draftApprovers(approvalStep).sort((a) => {
        if (a.status == STATUS.created) return -1
        return 0
      }) // we sort here because there's a check on the BE to prevent deleting an approver if just 1 exists, so we create first.

      return {
        step_id: approvalStep.id,
        status: approvalStep.status,
        approvers,
        position: stepDeleted ? null : (position += 1),
        approval_group_id: this.groupId,
      }
    })
  }
}

export default StepApprovers
